import {QueryOptions} from '@apollo/client';
import Backbone from 'backbone';

import {ArchiveRecord} from 'archive/ArchiveConstants';
import {ResultsFeature} from 'auth/capabilities/CapabilityFeatureConstants';
import {isFeatureEnabled} from 'auth/feature-enablement/FeatureStatus';
import {Permissions} from 'auth/PermissionConstants';
import BenchlingConfig from 'BenchlingConfig';
import * as statusbar from 'components/StatusBar';
import ModelTypeConstants from 'constants/ModelTypeConstants';
import FileState, {FileStateAttributes} from 'filestate/FileState';
import {getApolloClient} from 'graphql-client/ApolloClient';
import {graphQLToREST} from 'graphql-client/declarative-translations/external/translate';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import * as navigation from 'Navigation';
import StorableRelevantItemsTabState from 'relevant-items/storables/StorableRelevantItemsTabState';
import StorableResultsTabState from 'results/linked-results/StorableResultsTabState';
import {shouldDisablePrimaryLinkColumn} from 'runs/shouldDisablePrimaryLinkColumn';
import ContainerTestOrdersTabState from 'sample-tests/ui/test-orders/ContainerTestOrdersTabState';
import EntityStore from 'stores/EntityStore';
import {asJQueryPromise} from 'util/Promises';

import * as ContainerDAO from '../ContainerDAO';
import * as StorableActions from '../StorableActions';
import StorableStore from '../StorableStore';
import {ContainerViewQuery, ContainerViewQueryVariables} from './__generated/ContainerViewQuery';
import ContainerTabState from './ContainerTabState';
import {CONTAINER_VIEW_QUERY} from './ContainerViewGraphQL';

class ContainerToolbarState extends Backbone.Model {}

interface ContainerFileStateAttributes {
  name?: string;
  archiveRecord?: ArchiveRecord | null;
}
export default class ContainerFileState extends FileState {
  static fileType: string;
  static openFile: (fileId: string) => JQueryPromise<ContainerFileState>;
  static getQueryOptions(containerId: string): QueryOptions<ContainerViewQueryVariables> {
    return {
      query: CONTAINER_VIEW_QUERY,
      variables: {id: containerId},
    };
  }

  static dataToAttributes(data: ContainerViewQuery): ContainerFileStateAttributes {
    const containerNode = data.container;
    if (isInaccessible(containerNode)) {
      return {};
    }
    const container = graphQLToREST('Container', containerNode);
    return {
      name: container.name,
      archiveRecord: container.archiveRecord,
    };
  }

  override defaults(): FileStateAttributes {
    return {
      ...super.defaults(),
      toolbar: new ContainerToolbarState(),
      statusbar: this.hasResults()
        ? new statusbar.ContainerStatusBarState()
        : new statusbar.SingleBufferStatusBarState(),
    };
  }

  override editURL(): string {
    return ContainerDAO.containerEditorURL(this.id);
  }

  override name(): string {
    return this.get('name') || 'Loading...';
  }

  override archiveRecord(): ArchiveRecord {
    return this.get('archiveRecord');
  }

  override setArchiveRecord(archiveRecord: ArchiveRecord): void {
    this.set({archiveRecord});
  }

  override permissions(): Permissions {
    return this.get('permissions');
  }

  override setPermissions(permissions: Permissions): void {
    this.set({permissions});
  }

  hasResults(): boolean {
    return (
      isFeatureEnabled({feature: ResultsFeature.RESULTS_SHOW_DETAILS, item: {id: this.id}}) &&
      shouldDisablePrimaryLinkColumn() &&
      EntityStore.currentUser()
    );
  }

  hasTestOrders(): boolean {
    return BenchlingConfig.SAMPLE_TESTS_ENABLE;
  }
  hasRelevantItems(): boolean {
    return BenchlingConfig.INVENTORY_RELEVANT_ITEMS;
  }

  override initialize(...args: Array<unknown>): void {
    // @ts-expect-error
    super.initialize(...args);
    this.changeListener = this.changeListener.bind(this);
    this.registerListeners();
    this.changeListener();
    if (StorableStore.getContainer(this.id) == null) {
      StorableActions.fetchContainer(this.id);
    }

    const buffers = new navigation.BufferStates([new navigation.BufferState(), new navigation.BufferState()]);
    this.set({buffers});
    buffers
      .at(0)
      .tabs()
      .add(new ContainerTabState({containerId: this.id}));

    if (this.hasResults()) {
      buffers
        .at(1)
        .tabs()
        .add(new StorableResultsTabState({storableId: this.id}));
    }
    if (this.hasTestOrders()) {
      buffers
        .at(1)
        .tabs()
        .add(new ContainerTestOrdersTabState({modelId: this.id}));
    }
    if (this.hasRelevantItems()) {
      buffers
        .at(1)
        .tabs()
        .add(new StorableRelevantItemsTabState({storableId: this.id}));
    }
    this.initIsWorkspaceSplitFromPreference();
  }

  override registerTitleAndURLSync(): void {
    this.on('change:name', () => {
      this.syncTitleAndURL();
    });
  }

  changeListener(): void {
    const container = StorableStore.getContainer(this.id);
    if (container) {
      this.set({name: container.name, archiveRecord: container.archiveRecord});
    }
  }

  registerListeners(): void {
    // eslint-disable-next-line @typescript-eslint/unbound-method
    StorableStore.addChangeListener(this.changeListener);
  }

  cleanUp(): void {
    // eslint-disable-next-line @typescript-eslint/unbound-method
    StorableStore.removeChangeListener(this.changeListener);
  }
}

ContainerFileState.fileType = ModelTypeConstants.CONTAINER;

function fetchContainerViewAttributes(fileId: string): Promise<ContainerFileStateAttributes> {
  return getApolloClient()
    .query<ContainerViewQuery>(ContainerFileState.getQueryOptions(fileId))
    .then((response) => ContainerFileState.dataToAttributes(response.data))
    .catch(() => ({}));
}

ContainerFileState.openFile = (fileId: string): JQueryPromise<ContainerFileState> => {
  const container = StorableStore.getContainer(fileId);
  if (container != null) {
    const fileState = new ContainerFileState({
      id: fileId,
      name: container.name,
      archiveRecord: container.archiveRecord,
    });
    fetchContainerViewAttributes(fileId).then((attrs) => fileState.set(attrs));
    return asJQueryPromise(Promise.resolve(fileState));
  }

  return asJQueryPromise(
    fetchContainerViewAttributes(fileId).then((attrs) => new ContainerFileState({id: fileId, ...attrs}))
  );
};
